import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Paperclip, X } from 'lucide-react';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useOrgId } from '@/lib/org';
import {
  errorText,
  uploadFile,
  useAttachments,
  type Attachment,
  type EntityType,
} from '@/lib/portal';
import { FileDrop } from './file-drop';
import { FileLink } from './file-link';

/**
 * Attachments on a saved document, journal entry or payment (document 05, form layout 6): the
 * files, a drop zone to add more and remove for those who may change them.
 */
export function AttachmentsPanel({
  entityType,
  entityId,
  canChange,
}: {
  entityType: EntityType;
  entityId: string;
  canChange: boolean;
}) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const list = useAttachments(entityType, entityId);
  const key = ['portal', orgId, 'attachments', entityType, entityId];
  const add = useMutation({
    mutationFn: async (files: File[]) => {
      for (const f of files)
        await uploadFile<Attachment>(
          `/orgs/${orgId}/attachments?entityType=${entityType}&entityId=${entityId}`,
          f,
        );
    },
    onSuccess: (_r, files) =>
      toast(files.length === 1 ? `${files[0]!.name} attached.` : `${files.length} files attached.`),
    onError: (e) => toast(errorText(e), { tone: 'error' }),
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
  const remove = useMutation({
    mutationFn: (a: Attachment) =>
      api<void>(`/orgs/${orgId}/attachments/${a.attachmentId}`, { method: 'DELETE' }),
    onSuccess: (_r, a) => toast(`${a.fileName} removed.`),
    onError: (e) => toast(errorText(e), { tone: 'error' }),
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
  const files = list.data ?? [];
  if (!canChange && !files.length) return null;
  return (
    <section className="space-y-2" data-testid="attachments" aria-label="Attachments">
      <h3 className="flex items-center gap-1 text-sm font-medium">
        <Paperclip className="size-4 text-muted-foreground" aria-hidden /> Attachments
        {files.length ? <span className="text-muted-foreground">({files.length})</span> : null}
      </h3>
      {files.length ? (
        <ul className="space-y-1">
          {files.map((a) => (
            <li key={a.attachmentId} className="flex items-center gap-2">
              <FileLink file={a} />
              {canChange ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  aria-label={`Remove ${a.fileName}`}
                  disabled={remove.isPending}
                  onClick={() => {
                    if (window.confirm(`Remove ${a.fileName} from this record?`)) remove.mutate(a);
                  }}
                >
                  <X className="size-4" />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {canChange ? (
        <FileDrop
          compact
          disabled={add.isPending}
          label={add.isPending ? 'Uploading…' : 'Drop files to attach, or'}
          onFiles={(f) => add.mutate(f)}
          onProblem={(m) => toast(m, { tone: 'error' })}
        />
      ) : null}
    </section>
  );
}
